import type { PortalMe, PortalSession } from '@pm/schemas';
import { Avatar, Button, cn } from '@pm/ui';
import { useQueryClient } from '@tanstack/react-query';
import { FilePlus2, Inbox, ListTodo, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { BrandLogo, BrandMonogram } from '@/components/BrandMark';
import { hasLogo, usePublicBranding, useFavicon } from '@/lib/branding';
import {
  PortalAuthContext,
  portalSession,
  setPortalUnauthorizedHandler,
  usePortalAuth,
} from './portal-api';

/** Provedor de sessão do portal: envolve login, convite e área autenticada. */
export function PortalProvider() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [me, setMe] = useState<PortalMe | null>(() =>
    portalSession.token ? portalSession.me : null,
  );
  useEffect(() => {
    setPortalUnauthorizedHandler(() => {
      setMe(null);
      qc.clear();
      void navigate('/portal/login', { replace: true });
    });
    return () => setPortalUnauthorizedHandler(null);
  }, [navigate, qc]);
  return (
    <PortalAuthContext.Provider
      value={{
        me,
        signIn: (s: PortalSession) => {
          portalSession.set(s);
          setMe(
            portalSession.me ?? {
              contact: s.contact,
              client: s.client,
              organization: s.organization,
            },
          );
        },
        signOut: () => {
          portalSession.clear();
          qc.clear();
          setMe(null);
          void navigate('/portal/login', { replace: true });
        },
      }}
    >
      <Outlet />
    </PortalAuthContext.Provider>
  );
}

const NAV = [
  { to: '/portal', label: 'Demandas', icon: ListTodo, end: true },
  { to: '/portal/solicitacoes', label: 'Solicitações', icon: Inbox },
  { to: '/portal/solicitar', label: 'Nova solicitação', icon: FilePlus2 },
];

/** Layout da área autenticada do cliente: mais leve que o app interno, mesma linguagem visual. */
export function PortalShell() {
  const { me, signOut } = usePortalAuth();
  const location = useLocation();
  if (!me) return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  return (
    <div className="min-h-screen bg-bg text-text-1">
      <header className="sticky top-0 z-(--z-topbar) border-b border-border bg-bg/90 backdrop-blur">
        <div className="mx-auto flex h-topbar w-full max-w-[1100px] items-center gap-4 px-4">
          <PortalBrand me={me} />
          <nav className="ml-auto hidden items-center gap-1 sm:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cn(
                    'flex h-8 items-center gap-1.5 rounded px-2.5 text-sm text-text-2 hover:bg-surface-2 hover:text-text-1',
                    isActive && 'bg-accent-soft text-accent',
                  )
                }
              >
                <n.icon className="size-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Avatar name={me.contact.name} size="md" />
            <span className="hidden text-sm text-text-2 md:inline">{me.contact.name}</span>
            <Button variant="ghost" size="icon-sm" aria-label="Sair" onClick={signOut}>
              <LogOut />
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-border px-2 py-1 sm:hidden">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn(
                  'flex h-8 shrink-0 items-center gap-1.5 rounded px-2.5 text-sm text-text-2',
                  isActive && 'bg-accent-soft text-accent',
                )
              }
            >
              <n.icon className="size-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto w-full max-w-[1100px] px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

/** Logotipo da organização (ou monograma + nome) e, ao lado, de qual cliente é o portal. */
function PortalBrand({ me }: { me: PortalMe }) {
  const { data: brand } = usePublicBranding({ organization: me.organization.id });
  useFavicon(brand);
  const name = brand?.display_name ?? me.organization.name;
  const caption = (
    <p className="truncate font-mono text-[10px] uppercase tracking-[var(--tracking-caps)] text-text-3">
      Portal · {me.client.name}
    </p>
  );
  if (hasLogo(brand))
    return (
      <div className="flex min-w-0 items-center gap-3">
        <BrandLogo branding={brand} className="max-w-[180px] shrink-0" />
        <span className="h-6 w-px shrink-0 bg-border" aria-hidden />
        <div className="min-w-0 leading-tight">{caption}</div>
      </div>
    );
  return (
    <div className="flex min-w-0 items-center gap-2">
      <BrandMonogram name={name} className="size-7 text-xs" />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-medium">{name}</p>
        {caption}
      </div>
    </div>
  );
}
